import { useMemo } from 'react';
import {
  IConditionNode,
  IRulesetAction,
  IRulesetSchemaAction,
  IRulesetSchemaField,
  IRulesetSchemaOperator,
  isActionParamApplicable,
  isConditionGroup,
  isConditionNotGroup,
} from '../../../types/ruleset-types';

// Helper to get field identifier
export const getFieldId = (field: IRulesetSchemaField): string => {
  return (field as any).field_path ?? field.name ?? '';
};

// Helper to find a schema field by value (supports dynamic fields)
export const findSchemaField = (
  fields: IRulesetSchemaField[],
  fieldValue: string
): IRulesetSchemaField | undefined => {
  return fields.find((field) => {
    const fieldId = getFieldId(field);
    if (!fieldId) {
      return false;
    }
    if (fieldId === fieldValue) {
      return true;
    }
    return !!field.dynamic && fieldValue.startsWith(`${fieldId}.`);
  });
};

// Helper to get action type from schema
export const getActionType = (action: IRulesetSchemaAction): string => {
  return (action as any).type || action.name || '';
};

// Check if a value is valid for the given operator
const hasValueForOperator = (
  value: unknown,
  operator: IRulesetSchemaOperator
): boolean => {
  if (operator.value_type === 'none') {
    return true;
  }

  if (operator.value_type === 'range') {
    return (
      Array.isArray(value) &&
      value.length === 2 &&
      value[0] !== null &&
      value[0] !== undefined &&
      value[1] !== null &&
      value[1] !== undefined
    );
  }

  if (operator.value_type === 'list' || operator.value_type === 'regex_list') {
    return Array.isArray(value) && value.length > 0;
  }

  return value !== null && value !== undefined && value !== '';
};

// Validate condition tree recursively
const validateConditions = (
  node: IConditionNode,
  fields: IRulesetSchemaField[],
  isRoot: boolean = false
): string[] => {
  if (isConditionGroup(node)) {
    if (isConditionNotGroup(node)) {
      return validateConditions(node.condition, fields, false);
    }

    if (node.conditions.length === 0) {
      return isRoot
        ? ['At least one condition is required']
        : [`${node.type.toUpperCase()} groups require at least 2 conditions`];
    }

    if (!isRoot && node.conditions.length < 2) {
      return [`${node.type.toUpperCase()} groups require at least 2 conditions`];
    }

    return node.conditions.flatMap((child) => validateConditions(child, fields, false));
  }

  const field = findSchemaField(fields, node.field);
  if (!node.field || !field) {
    return ['Select a valid field'];
  }

  const fieldId = getFieldId(field);
  if (field.dynamic && node.field === fieldId) {
    return ['Provide a key for the selected dynamic field'];
  }

  const operator = field.operators.find((item) => item.name === node.operator);
  if (!node.operator || !operator) {
    return ['Select a valid operator'];
  }

  if (
    node.modifiers?.case_insensitive &&
    !operator.modifiers?.includes('case_insensitive')
  ) {
    return ['Case-insensitive matching is not available for this operator'];
  }

  if (!hasValueForOperator(node.value, operator)) {
    return ['Provide a value for the selected operator'];
  }

  return [];
};

// Validate actions array
const validateActions = (
  actions: IRulesetAction[],
  schemaActions: IRulesetSchemaAction[]
): string[] => {
  if (actions.length === 0) {
    return ['At least one action is required'];
  }

  const actionTypes = actions.map((action) => action.type);
  if (new Set(actionTypes).size !== actionTypes.length) {
    return ['Each action type may only be used once'];
  }

  return actions.flatMap((action) => {
    const schemaAction = schemaActions.find((item) => getActionType(item) === action.type);
    if (!schemaAction) {
      return [`Unknown action type: ${action.type}`];
    }

    const params = Array.isArray(schemaAction.params)
      ? schemaAction.params.map((param) => [param.name, param] as const)
      : Object.entries(schemaAction.params || {});

    return params.flatMap(([paramName, paramConfig]) => {
      if ((paramConfig as any).required === false) {
        return [];
      }

      // A param the action has retired is not a missing value. Without this,
      // a set_status rule targeting anything but EXCLUDED cannot be saved
      // until the operator picks an exclusion reason the API then discards.
      if (!isActionParamApplicable(action, paramName)) {
        return [];
      }

      const value = action[paramName];
      return value === null || value === undefined || value === ''
        ? [`Provide a value for "${paramName}" on ${schemaAction.label}`]
        : [];
    });
  });
};

interface UseRulesetValidationParams {
  label: string;
  conditions: IConditionNode;
  actions: IRulesetAction[];
  schemaFields: IRulesetSchemaField[];
  schemaActions: IRulesetSchemaAction[];
}

interface UseRulesetValidationResult {
  errors: string[];
  isValid: boolean;
  firstError: string;
}

export const useRulesetValidation = ({
  label,
  conditions,
  actions,
  schemaFields,
  schemaActions,
}: UseRulesetValidationParams): UseRulesetValidationResult => {
  const errors = useMemo(() => {
    return [
      ...(label.trim() ? [] : ['Label is required']),
      ...validateConditions(conditions, schemaFields, true),
      ...validateActions(actions, schemaActions),
    ];
  }, [label, conditions, actions, schemaFields, schemaActions]);

  return {
    errors,
    isValid: errors.length === 0,
    firstError: errors[0] || 'Please fill in all required fields',
  };
};

export default useRulesetValidation;
